/**
 * @file ProductRangeFilter.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-23
 * @modified 2026-09-23
 *
 * "Product Range" — the Xero account code a document's lines post to, as a
 * filter.
 *
 * The account code IS the product range in this business: 201 is compounds,
 * 206 minerals, 200g dog food. Staff think in ranges and the system stored the
 * range on every line all along without ever offering it as a filter, so
 * "show me the compound orders" meant reading the whole list.
 *
 * Which code depends on the document. An invoice is a sale, so it filters on
 * the SALES account; a purchase order and a customer order are both about what
 * we buy, so they filter on the PURCHASE account. Getting that backwards would
 * silently return nothing, which is why the side is a required prop rather
 * than a default.
 *
 * The chart of accounts is fetched once per side and shared across every
 * screen that mounts one of these — it changes about as often as the company
 * does, and three search screens each fetching it on mount is three requests
 * for one answer.
 *
 * MULTI-select, because the question is usually a group rather than one range:
 * "the compound and blend orders" is two codes, and a single-value picker made
 * that two searches whose results had to be held in the head. An empty
 * selection means every range — the absence of a filter, not the absence of
 * results — which is why the trigger says "All Product Ranges" when nothing is
 * ticked.
 */

import React, { useEffect, useState } from 'react';
import { API_URL } from '../config';
import MultiSelectDropdown from './MultiSelectDropdown';
import { ACCOUNT_NAMES } from '../utils/accountCodes';

// code -> { promise, accounts }. Module-level, so the fetch is shared.
const cache = {};

const ENDPOINTS = {
  sales: `${API_URL}/api/inventory/sales-accounts`,
  purchase: `${API_URL}/api/inventory/purchase-accounts`
};

async function loadAccounts(side) {
  if (!cache[side]) {
    cache[side] = fetch(ENDPOINTS[side], { credentials: 'include' })
      .then(res => res.json())
      .then(data => (data.accounts || [])
        // Tolerate the pre-2026 shape (bare code strings) so a stale server
        // during a rolling deploy blanks the dropdown rather than throwing.
        .map(a => (typeof a === 'string' ? { code: a, name: null } : a))
        .filter(a => a && a.code))
      .catch((error) => {
        console.error(`Failed to load ${side} accounts:`, error);
        // Not cached as a rejection — a later mount should be able to retry.
        delete cache[side];
        return [];
      });
  }
  return cache[side];
}

/**
 * @param {'sales'|'purchase'} side  Which account the document's lines carry.
 * @param {string[]} value           Selected codes; [] is every range.
 * @param {function} onChange        (codes) => void.
 * @param {string} [label='Product Range']
 */
function ProductRangeFilter({ side, value, onChange, label = 'Product Range' }) {
  const [accounts, setAccounts] = useState(cache[`${side}:resolved`] || []);

  useEffect(() => {
    let cancelled = false;
    loadAccounts(side).then((list) => {
      cache[`${side}:resolved`] = list;
      if (!cancelled) setAccounts(list);
    });
    return () => { cancelled = true; };
  }, [side]);

  const options = accounts.map(a => {
    // The chart of accounts is the authority on the name; ACCOUNT_NAMES is the
    // fallback for a code the chart no longer carries but documents do.
    const name = a.name || ACCOUNT_NAMES[a.code] || '';
    return {
      value: String(a.code),
      badge: String(a.code),
      label: name || String(a.code),
      searchText: `${a.code} ${name}`
    };
  });

  /*
   * Tolerates the single string a persisted filter slot may still hold from
   * before this was a multi-select — and the 'all' sentinel that used to mean
   * "no filter", which is now an empty array.
   */
  const selected = Array.isArray(value)
    ? value
    : (value && value !== 'all' ? [String(value)] : []);

  return (
    <div className="filter-group">
      <label>{label}</label>
      <MultiSelectDropdown
        options={options}
        value={selected}
        onChange={onChange}
        placeholder="All Product Ranges"
        searchable
        searchPlaceholder="Search code or name…"
        // The filter row is a fixed-height grid cell; chips below the trigger
        // would push the row open as ranges are ticked.
        hideTags
        summarize={(vals) => {
          if (!vals.length) return 'All Product Ranges';
          if (vals.length === 1) {
            const opt = options.find(o => o.value === vals[0]);
            return opt ? `${opt.value} — ${opt.label}` : vals[0];
          }
          return `${vals.length} ranges`;
        }}
      />
    </div>
  );
}

/**
 * The selection as a query-string value: comma-separated codes, or '' for no
 * filter. Exported so the three search screens serialise it the same way and
 * the server has one shape to parse.
 *
 * Tolerates the bare string a filter slot saved before this was a
 * multi-select, and the old 'all' sentinel.
 */
export function productRangeParam(value) {
  const list = Array.isArray(value)
    ? value
    : (value && value !== 'all' ? [String(value)] : []);
  return list.filter(Boolean).join(',');
}

export default ProductRangeFilter;
